<template>
    <v-card>
        <v-card-title primary-title>Test Confluence Integration</v-card-title>
        <v-card-text>
            <v-card class="mb-4">
                <v-card-title> List folders </v-card-title>
                <v-card-text>
                    <v-row>
                        <v-col cols="12" md="4">
                            <v-autocomplete
                                v-model="parentPlatformFolder"
                                :items="platforms"
                                item-title="name"
                                return-object
                                label="Select Platform"
                                outlined
                            ></v-autocomplete>
                        </v-col>
                        <v-col md="4">
                            <v-btn :disabled="!parentPlatformFolder" @click="testListFolders"
                                >Test List Folders</v-btn
                            >
                        </v-col>
                    </v-row>
                    <v-row v-if="folderList.length > 0">
                        <v-col cols="12">
                            <v-list>
                                <v-list-item-group>
                                    <v-list-item v-for="folder in folderList" :key="folder.id">
                                        <v-list-item-content>
                                            <v-list-item-title>{{
                                                folder.title
                                            }}</v-list-item-title>
                                        </v-list-item-content>
                                    </v-list-item>
                                </v-list-item-group>
                            </v-list>
                        </v-col>
                    </v-row>
                    <v-row v-else-if="folderList.length === 0">
                        <v-col cols="12">
                            <p>No folders found.</p>
                        </v-col>
                    </v-row>
                </v-card-text>
            </v-card>
            <v-card class="mb-4">
                <v-card-title>Find or create folder for experiment group</v-card-title>
                <v-card-text>
                    <v-row>
                        <v-col cols="12" md="4">
                            <v-select
                                v-model="selectedExperimentGroup"
                                :items="experimentGroups"
                                item-title="name"
                                return-object
                                label="Select Experiment Group"
                                outlined
                            ></v-select>
                        </v-col>
                        <v-col md="4">
                            <v-btn
                                :disabled="!selectedExperimentGroup"
                                @click="testFindOrCreateFolder"
                                >Test Find or Create Folder</v-btn
                            >
                        </v-col>
                    </v-row>
                    <v-row v-if="experimentGroupFolder">
                        <v-col cols="12">
                            <p>
                                Experiment Group Folder: {{ experimentGroupFolder.title }} ({{
                                    experimentGroupFolder.id
                                }})
                            </p>
                        </v-col>
                    </v-row>
                </v-card-text>
            </v-card>
            <v-card class="mb-4">
                <v-card-title>Get template by ID</v-card-title>
                <v-card-text>
                    <v-row>
                        <v-col cols="12" md="4">
                            <v-autocomplete
                                v-model="selectedTemplateIdForRetrieve"
                                :items="allTemplates"
                                item-title="name"
                                item-value="templateId"
                                label="Select Template"
                                outlined
                            ></v-autocomplete>
                        </v-col>
                        <v-col md="4">
                            <v-btn :disabled="!selectedTemplateIdForRetrieve" @click="testGetTemplateById"
                                >Test Get Template by ID</v-btn
                            >
                        </v-col>
                    </v-row>
                    <v-row v-if="template">
                        <v-col cols="12">
                            <p>{{ template.name }} ({{ template.templateId }})</p>
                            <p><strong>Template body:</strong></p>
                            <p>{{ template.body.storage.value }}</p>
                        </v-col>
                    </v-row>
                    <v-row v-else>
                        <v-col cols="12">
                            <p>No template found.</p>
                        </v-col>
                    </v-row>
                </v-card-text>
            </v-card>
            <v-card class="mb-4">
                <v-card-title>Create test page from template</v-card-title>
                <v-card-text>
                    <v-row>
                        <v-col cols="12" md="4">
                            <v-autocomplete
                                v-model="selectedTemplateIdForCreate"
                                :items="allTemplates"
                                item-title="name"
                                item-value="templateId"
                                label="Select Template"
                                outlined
                            ></v-autocomplete>
                        </v-col>
                        <v-col md="4">
                            <v-btn :disabled="!selectedTemplateIdForCreate" @click="testCreatePageFromTemplate"
                                >Create Test Page</v-btn
                            >
                        </v-col>
                    </v-row>
                    <v-row v-if="pageCreatedFromTemplate">
                        <v-col cols="12">
                            <p>
                                Page created from template: {{
                                    pageCreatedFromTemplate.title
                                }} ({{ pageCreatedFromTemplate.confluenceUrl }})
                            </p>
                        </v-col>
                    </v-row>
                    <v-row v-else>
                        <v-col cols="12">
                            <p>No page created.</p>
                        </v-col>
                    </v-row>
                </v-card-text>
            </v-card>
            <v-card class="mb-4">
                <v-card-title>Fetch Page by ID</v-card-title>
                <v-card-text>
                    <v-row>
                        <v-col cols="12" md="4">
                            <v-text-field
                                v-model="pageIdToFetch"
                                label="Enter Page ID"
                                outlined
                            ></v-text-field>
                        </v-col>
                        <v-col md="4">
                            <v-btn :disabled="!pageIdToFetch" @click="fetchPageById">Fetch Page</v-btn>
                        </v-col>
                    </v-row>
                    <v-row v-if="fetchedPage">
                        <v-col cols="12">
                            <p>Fetched Page: {{ fetchedPage.title }} ({{ fetchedPage.id }})</p>
                            <p><strong>Content:</strong></p>
                            <div>{{ fetchedPage }}</div>
                        </v-col>
                    </v-row>
                </v-card-text>
            </v-card>
        </v-card-text>
    </v-card>
</template>

<script setup lang="ts">
import { onMounted, ref } from "vue";
import {
    BaseConfluenceTemplate,
    confluenceAPI,
    ConfluenceFolder,
    ConfluenceTemplate,
    Page,
    SavedConfluenceExperimentPage,
} from "@/services/confluence/confluenceAPI";
import { PlatformDTO } from "@/services/commonModels";
import { withLoadingScreen } from "@/lib/loading";
import {
    ExperimentGroupDTO,
    experimentGroupsAPI,
} from "@/services/experimentGroups/experimentGroupsAPI";
import { experimentsAPI } from "@/services/experiments/experimentsAPI";

const platforms = ref<PlatformDTO[]>([]);
const parentPlatformFolder = ref<PlatformDTO | null>(null);
const folderList = ref<ConfluenceFolder[]>([]);
const experimentGroups = ref<ExperimentGroupDTO[]>([]);
const selectedExperimentGroup = ref<ExperimentGroupDTO | null>(null);
const experimentGroupFolder = ref<ConfluenceFolder | null>(null);
const allTemplates = ref<BaseConfluenceTemplate[]>([]);
const selectedTemplateIdForRetrieve = ref<string | null>(null);
const template = ref<ConfluenceTemplate | null>(null);
const selectedTemplateIdForCreate = ref<string | null>(null);
const pageCreatedFromTemplate = ref<SavedConfluenceExperimentPage | null>(null);
const pageIdToFetch = ref<string | null>(null);
const fetchedPage = ref<Page | null>(null);

onMounted(async () => {
    await withLoadingScreen(async () => {
        const allExperimentGroups = await experimentGroupsAPI.fetchExperimentGroups();
        experimentGroups.value = allExperimentGroups.filter((experimentGroup) => {
            return experimentGroup.isAvailableInExperimentSetup;
        });
        platforms.value = await experimentsAPI.fetchPlatforms();
        allTemplates.value = await confluenceAPI.getAllTemplates();
    });
});

async function testListFolders() {
    folderList.value = await confluenceAPI.listFolders(parentPlatformFolder.value!.folderId);
}

async function testFindOrCreateFolder() {
    if (!selectedExperimentGroup.value) return;

    experimentGroupFolder.value = await confluenceAPI.findOrCreateExperimentGroupFolder(
        selectedExperimentGroup.value.code
    );
}

async function testGetTemplateById() {
    if (!selectedTemplateIdForRetrieve.value) return;

    template.value = await confluenceAPI.getTemplateById(selectedTemplateIdForRetrieve.value);
}

async function testCreatePageFromTemplate() {
    if (!selectedTemplateIdForCreate.value) return;

    pageCreatedFromTemplate.value = await confluenceAPI.createTestPageFromTemplate(selectedTemplateIdForCreate.value);
}

async function fetchPageById() {
    if (!pageIdToFetch.value) return;

    fetchedPage.value = await confluenceAPI.fetchPageById(pageIdToFetch.value);
}
</script>
